import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Layers, CheckSquare, User, MoreHorizontal } from "lucide-react";
import { cn } from "@/lib/utils";
import { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { 
  FileText, 
  PlayCircle,
  BarChart3, 
  Settings, 
  LogOut,
  Bot
} from "lucide-react";
import { RitualIcon } from "@/components/ui/RitualIcon";
import { supabase } from "@/integrations/supabase/client";
import { AgentAvatar } from "@/components/agent/AgentAvatar";

// Simplified 3-item navigation: Feed, Library, Me
const navItems = [
  { title: "Feed", url: "/app", icon: Layers },
  { title: "Library", url: "/app/library", icon: FileText },
  { title: "Me", url: "/app/settings", icon: User },
];

// Custom icon component wrapper for RitualIcon
const AISetupIcon = () => <RitualIcon size="sm" />;

const menuItems = [
  { title: "Tasks", url: "/app/tasks", icon: CheckSquare },
  { title: "Runs", url: "/app/runs", icon: PlayCircle },
  { title: "AI Setup", url: "/app/setup", icon: AISetupIcon },
  { title: "Analytics", url: "/app/analytics", icon: BarChart3 },
];

export function MobileBottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const isActive = (path: string) => {
    if (path === "/app") {
      return location.pathname === "/app" || location.pathname === "/app/feed";
    }
    if (path === "/app/library") {
      return location.pathname.startsWith("/app/library") || 
             location.pathname.startsWith("/app/sops");
    }
    return location.pathname.startsWith(path);
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/auth");
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-lg border-t md:hidden safe-area-bottom">
      <div className="flex items-center justify-around h-16 max-w-md mx-auto">
        {navItems.map((item) => (
          <NavLink
            key={item.title}
            to={item.url}
            className={cn(
              "flex flex-col items-center justify-center flex-1 h-full gap-1 transition-all touch-manipulation",
              isActive(item.url)
                ? "text-primary"
                : "text-muted-foreground active:text-foreground"
            )}
          >
            <div className={cn(
              "flex items-center justify-center rounded-xl p-1.5 transition-colors",
              isActive(item.url) && "bg-primary/10"
            )}>
              <item.icon className={cn("h-5 w-5", isActive(item.url) && "scale-110")} />
            </div>
            <span className="text-[10px] font-medium">{item.title}</span>
          </NavLink>
        ))}
        
        <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
          <SheetTrigger asChild>
            <button
              className={cn(
                "flex flex-col items-center justify-center flex-1 h-full gap-1 transition-all touch-manipulation",
                menuOpen ? "text-primary" : "text-muted-foreground active:text-foreground"
              )}
            >
              <div className={cn(
                "flex items-center justify-center rounded-xl p-1.5 transition-colors",
                menuOpen && "bg-primary/10"
              )}>
                <MoreHorizontal className="h-5 w-5" />
              </div>
              <span className="text-[10px] font-medium">More</span>
            </button>
          </SheetTrigger>
          <SheetContent side="bottom" className="h-auto max-h-[75vh] rounded-t-2xl">
            <SheetHeader className="pb-4">
              <SheetTitle className="text-left text-lg">Menu</SheetTitle>
            </SheetHeader>
            
            {/* Menu Grid */}
            <div className="grid grid-cols-3 gap-3 pb-4">
              {menuItems.map((item) => (
                <Button
                  key={item.title}
                  variant={isActive(item.url) ? "secondary" : "outline"}
                  className="h-20 flex-col gap-2 border-border/50"
                  onClick={() => {
                    navigate(item.url);
                    setMenuOpen(false);
                  }}
                >
                  <item.icon className="h-5 w-5" />
                  <span className="text-xs">{item.title}</span>
                </Button>
              ))}
            </div>

            {/* Agent Status Card */}
            <div className="flex items-center gap-3 p-3 rounded-xl bg-primary/5 border border-primary/20 mb-4">
              <AgentAvatar size="sm" />
              <div className="flex-1">
                <p className="text-sm font-medium">Ritual Agent</p>
                <p className="text-xs text-muted-foreground">Ready to help</p>
              </div>
              <div className="h-2 w-2 rounded-full bg-success animate-pulse" />
            </div>
            
            {/* Sign Out */}
            <Button
              variant="ghost"
              className="w-full text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={handleSignOut}
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </Button>
          </SheetContent>
        </Sheet>
      </div>
    </nav>
  );
}
